import type { AppSettings, ChatMessage, InferenceMessage, ModelDefinition } from "../types";
import { attachmentBlock } from "./files";

const DEFAULT_SYSTEM_PROMPT =
  "あなたは正確で実用的なAIアシスタントです。根拠のない断定を避け、分からないことは分からないと伝えてください。";

function renderMessage(message: ChatMessage): string {
  const content = stripUnsafeControlCharacters(message.content);
  if (!message.attachments?.length) return content;
  const attachments = message.attachments.map(attachmentBlock).join("\n\n");
  return `${content}\n\n${stripUnsafeControlCharacters(attachments)}`.trim();
}

function stripUnsafeControlCharacters(value: string): string {
  return value.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/gu, "");
}

function takeWithinApproximateTokenBudget(
  messages: ChatMessage[],
  maxMessages: number,
  contextTokens: number,
): ChatMessage[] {
  const characterBudget = Math.max(2_000, contextTokens * 3);
  const selected: ChatMessage[] = [];
  let used = 0;

  for (const message of messages.slice(-maxMessages).reverse()) {
    const size = renderMessage(message).length;
    if (selected.length > 0 && used + size > characterBudget) break;
    selected.unshift(message);
    used += size;
  }
  return selected;
}

export function buildInferenceMessages(
  history: ChatMessage[],
  settings: AppSettings,
  model: ModelDefinition,
): InferenceMessage[] {
  const recent = takeWithinApproximateTokenBudget(
    history,
    settings.historyLimit,
    settings.contextTokens,
  );
  const systemParts = [settings.systemPrompt.trim() || DEFAULT_SYSTEM_PROMPT];
  if (settings.customContext.trim()) {
    systemParts.push(`常設コンテキスト:\n${settings.customContext.trim()}`);
  }
  const system = systemParts.join("\n\n");
  const converted = recent
    .map<InferenceMessage>((message) => ({
      role: message.role,
      content: renderMessage(message).trim(),
    }))
    .filter((message) => message.content.length > 0);

  if (model.promptAdapter === "deepseek" || model.family === "Gemma 4") {
    const firstUser = converted.findIndex((message) => message.role === "user");
    if (firstUser >= 0) {
      converted[firstUser] = {
        role: "user",
        content: `回答ルール:\n${system}\n\nユーザー入力:\n${converted[firstUser].content}`,
      };
    }
    return converted;
  }

  return [{ role: "system", content: system }, ...converted];
}

export function defaultSettings(): AppSettings {
  return {
    mode: "medium",
    contextTokens: 4096,
    historyLimit: 12,
    customContext: "",
    systemPrompt: DEFAULT_SYSTEM_PROMPT,
    ragEnabled: true,
    temperature: 0.6,
    maxNewTokens: 512,
  };
}
